import { Music } from "lucide-react";

import { type MouseEvent, useState } from "react";

import { cn } from "~/shared/lib/utils";

export interface MediaSurfaceProps {
    url: string;
    /**
     * Тип медиа: картинка (`<img>`), видео (`<video controls>`) или звук
     * (`<audio controls>`). У звука нет кадра, поэтому вместо него рисуется
     * подпись с иконкой — иначе прикреплённое аудио можно только скачать.
     */
    type: "image" | "video" | "audio";
    /** Автовоспроизведение видео и аудио (лайтбокс — да; статичный просмотр — по вкусу). */
    autoPlay?: boolean;
    /** alt изображения / подпись. */
    caption?: string;
    className?: string;
    onClick?: (e: MouseEvent) => void;
}

/**
 * Презентационный примитив одного медиа: `<img>` или `<video controls>` по `type`, единый
 * crop `object-contain`. Общий рендер для лайтбокса (`shared/ui/media-lightbox`) и страницы
 * ревью (`widgets/review`) — чтобы правило показа одного кадра жило в одном месте.
 */
export function MediaSurface({
    url,
    type,
    autoPlay = false,
    caption,
    className,
    onClick,
}: MediaSurfaceProps) {
    // Держимся за URL, а не за одно число: при листании ленты слайд меняется на
    // месте, и отношение пришлось бы сбрасывать эффектом — забытый сброс оставил
    // бы новый ролик в рамке предыдущего.
    const [measured, setMeasured] = useState<{ url: string; ratio: number } | null>(null);
    const base = "max-h-full max-w-full rounded-sm object-contain shadow-2xl";
    if (type === "audio") {
        // У звука нечего показать кадром: даём плеер и имя файла, чтобы человек
        // понимал, что именно слушает, когда в ленте несколько дорожек.
        return (
            <div
                onClick={onClick}
                className={cn(
                    "flex w-full max-w-lg flex-col items-center gap-4 rounded-lg bg-zinc-900/80 p-6 shadow-2xl",
                    className,
                )}
            >
                <Music className="h-10 w-10 text-violet-300" aria-hidden />
                {caption && <span className="max-w-full truncate text-sm text-zinc-300">{caption}</span>}
                <audio key={url} src={url} className="w-full" controls autoPlay={autoPlay} />
            </div>
        );
    }
    if (type === "video") {
        // Пока размер ролика неизвестен, у `<video>` нет своих пропорций, и
        // браузер рисует плеер в дефолтные 300×150 — посреди чёрного экрана это
        // крошечное окошко. Поэтому плеер всегда занимает область показа целиком
        // при своём отношении сторон: ширина упирается либо в ширину области,
        // либо в её высоту, пересчитанную по этому отношению (`cqh` — проценты
        // высоты контейнера, потому он и объявлен размерным). До метаданных
        // отношение берём типовое, 16:9, и у такого ролика — а это почти все
        // наши — размер плеера с приходом кадра не меняется вовсе: заполняется
        // ровно та же рамка, без скачка, заметного на медленной сети. Ролик
        // другой формы перестраивает рамку один раз, по метаданным, задолго до
        // самого кадра. Своего ожидания не рисуем: плеер крутит собственное, и
        // два спиннера на одном месте выглядят ошибкой.
        const ratio = measured?.url === url ? measured.ratio : 16 / 9;
        return (
            <div className="flex h-full w-full items-center justify-center" style={{ containerType: "size" }}>
                <video
                    key={url}
                    src={url}
                    onClick={onClick}
                    onLoadedMetadata={(e) => {
                        const { videoWidth: w, videoHeight: h } = e.currentTarget;
                        // Нули отдаёт битый файл: типовая рамка там лучше, чем
                        // схлопнутый в ничто плеер.
                        if (w > 0 && h > 0) setMeasured({ url, ratio: w / h });
                    }}
                    className={cn(base, className)}
                    style={{ aspectRatio: String(ratio), width: `min(100%, calc(100cqh * ${ratio}))` }}
                    controls
                    autoPlay={autoPlay}
                />
            </div>
        );
    }
    return (
        <img
            src={url}
            alt={caption ?? ""}
            onClick={onClick}
            className={cn(base, className)}
            // Кадр, который открыли, обгоняет ленивую догрузку ленты: на хост
            // всего шесть соединений, и без этого нажатая картинка ждала бы
            // очереди за теми, что человек уже не смотрит. У видео такого рычага
            // нет — `fetchpriority` браузеры принимают у картинок и скриптов, но
            // не у `<video>`; жадный `preload="auto"` там тоже не помогает
            // (воспроизведение и так тянет данные), а канал занимает.
            fetchPriority="high"
            loading="eager"
        />
    );
}
